import { useMutation, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { api } from '@/modules/common/api/index.ts';
import { REGISTER_ID_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import { useGetRegisterIdParam } from '@/modules/common/hooks/useGetRegisterIdParam.ts';
import { queryClient } from '@/modules/common/query.ts';
import { withErrorHandling } from '@/modules/common/utils/error-utils.tsx';
import { ISSUER_ENTITIES_QUERY_KEY } from '@/modules/issuer-dashboard/queries/entities.ts';
import {
  AddExistingInvestorToRegisterRequest,
  AddExistingInvestorToRegisterResponse,
  GetBalanceByDateResponse,
  GetBalanceStatisticsResponse,
  GetIssuerBalanceStatisticsV1SrIssuerBalanceStatisticsGetParams,
  GetIssuerTopHoldersResponse,
  GetRegisterShareholderListV1SrShareholdersByYourRegisterGetParams,
  GetShareholderHistoryListV1SrShareholdersHoldingsValueHistoryGetParams,
  GetShareholdersByRegisterResponse,
  GetShareholdersSubscriptionsClassesResponse,
  GetShareholdersSubscriptionsClassesV1SrShareholdersSubscriptionClassesGetParams,
  GetTopHoldersChartV1SrChartsIssuerTopHoldersGetParams,
  GetTotalShareholderHistoryResponse,
  IssuerBalancesListV1SrIssuerBalancesGetParams,
} from 'api';
import { ISSUER_COMMUNICATION_PREFERENCES_QUERY_KEY } from '@/modules/issuer-dashboard/queries/charts.ts';

export const SHAREHOLDERS_HOLDINGS_QUERY_KEY = 'vestra/shareholders-holdings-query';
export const TOP_HOLDERS_QUERY_KEY = 'vestra/top-holders-query';
export const SHAREHOLDER_BALANCE_STATISTIC_QUERY_KEY =
  'vestra/issuer-shareholder-balance-statistic';
export const SHAREHOLDER_BALANCE_QUERY_KEY = 'vestra/issuer-shareholder-balance';
export const SHAREHOLDERS_BY_IDS_SUBSCRIPTIONS_CLASSES_QUERY_KEY =
  'vestra/shareholders-by-ids-subscriptions-classes';
export const REGISTER_SHAREHOLDERS_QUERY_KEY = 'vestra/register-shareholders';

export const useShareholdersHoldingsQuery = (
  payload: GetShareholderHistoryListV1SrShareholdersHoldingsValueHistoryGetParams,
  disabled: boolean,
  register_id: string,
) => {
  return useQuery<GetTotalShareholderHistoryResponse>({
    queryKey: [SHAREHOLDERS_HOLDINGS_QUERY_KEY, JSON.stringify(payload), register_id],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response =
        await api.v1.getShareholderHistoryListV1SrShareholdersHoldingsValueHistoryGet({
          ...payload,
        });
      return response.data;
    },
    enabled: !disabled,
  });
};

export const useTopHoldersQuery = (
  payload: GetTopHoldersChartV1SrChartsIssuerTopHoldersGetParams,
  disabled: boolean,
) => {
  const [params] = useSearchParams();

  return useQuery<GetIssuerTopHoldersResponse>({
    queryKey: [
      TOP_HOLDERS_QUERY_KEY,
      JSON.stringify(payload),
      params.get(REGISTER_ID_SEARCH_PARAM_KEY) || '',
    ],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getTopHoldersChartV1SrChartsIssuerTopHoldersGet({
        ...payload,
      });
      return response.data;
    },
    enabled: !disabled,
  });
};

export const useShareholderBalanceStatisticsQuery = (
  register_id: string,
  params: GetIssuerBalanceStatisticsV1SrIssuerBalanceStatisticsGetParams,
  disabled = false,
) => {
  return useQuery<GetBalanceStatisticsResponse>({
    queryKey: [SHAREHOLDER_BALANCE_STATISTIC_QUERY_KEY, register_id, JSON.stringify(params)],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getIssuerBalanceStatisticsV1SrIssuerBalanceStatisticsGet(
        params,
      );
      return response.data;
    },
    enabled: !disabled,
  });
};

export const useShareholderChartBalanceQuery = (
  payload: IssuerBalancesListV1SrIssuerBalancesGetParams,
  register_id: number,
  disabled = false,
) => {
  return useQuery<GetBalanceByDateResponse>({
    queryKey: [SHAREHOLDER_BALANCE_QUERY_KEY, register_id, JSON.stringify(payload)],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.issuerBalancesListV1SrIssuerBalancesGet(payload);

      return response.data;
    },
    enabled: !disabled,
  });
};

export const useShareholdersByIdsSubscriptionsClasses = (
  payload: GetShareholdersSubscriptionsClassesV1SrShareholdersSubscriptionClassesGetParams,
  disabled = false,
) => {
  return useQuery<GetShareholdersSubscriptionsClassesResponse>({
    queryKey: [
      SHAREHOLDERS_BY_IDS_SUBSCRIPTIONS_CLASSES_QUERY_KEY,
      useGetRegisterIdParam(),
      JSON.stringify(payload),
    ],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response =
        await api.v1.getShareholdersSubscriptionsClassesV1SrShareholdersSubscriptionClassesGet(
          payload,
        );

      return response.data;
    },
    enabled: !disabled,
  });
};

export const useRegisterShareholdersQuery = (
  payload: GetRegisterShareholderListV1SrShareholdersByYourRegisterGetParams,
  disabled = false,
) => {
  return useQuery<GetShareholdersByRegisterResponse>({
    queryKey: [REGISTER_SHAREHOLDERS_QUERY_KEY, JSON.stringify(payload)],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getRegisterShareholderListV1SrShareholdersByYourRegisterGet(
        payload,
      );
      return response.data;
    },
    enabled: !disabled,
  });
};

export const useAddExistingInvestorMutation = ({
  onSuccess,
}: {
  onSuccess?: () => void;
} = {}) => {
  return useMutation({
    mutationFn: async (payload: AddExistingInvestorToRegisterRequest) => {
      const response = await withErrorHandling(
        api.instance.post<AddExistingInvestorToRegisterResponse>(
          '/v1/sr/shareholders/add-existing',
          payload,
        ),
        {
          loading: 'Adding existing investor...',
          success: 'Investor successfully added to fund',
        },
      );

      return response.data;
    },
    onSuccess: (_data, variables) => {
      const { target_register_id } = variables;

      queryClient.invalidateQueries({
        queryKey: [
          REGISTER_SHAREHOLDERS_QUERY_KEY,
          JSON.stringify({ register_id: target_register_id }),
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [TOP_HOLDERS_QUERY_KEY, target_register_id],
      });
      queryClient.invalidateQueries({
        queryKey: [SHAREHOLDERS_HOLDINGS_QUERY_KEY, target_register_id],
      });
      queryClient.invalidateQueries({
        queryKey: [SHAREHOLDER_BALANCE_QUERY_KEY, target_register_id],
      });
      queryClient.invalidateQueries({
        queryKey: [SHAREHOLDER_BALANCE_STATISTIC_QUERY_KEY, target_register_id],
      });
      queryClient.invalidateQueries({
        queryKey: [SHAREHOLDERS_BY_IDS_SUBSCRIPTIONS_CLASSES_QUERY_KEY, target_register_id],
      });
      queryClient.invalidateQueries({ queryKey: [ISSUER_ENTITIES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [REGISTER_ID_SEARCH_PARAM_KEY] });
      queryClient.invalidateQueries({ queryKey: [ISSUER_COMMUNICATION_PREFERENCES_QUERY_KEY] });
      onSuccess?.();
    },
  });
};
